import _ from 'lodash';
import { Opt } from './general.types';

export default class BiMap<K, V> {
  private forwardMap: Map<K, V>;
  private reverseMap: Map<V, K>;

  constructor(source?: readonly (readonly [K, V])[]) {
    if (!source) {
      this.forwardMap = new Map<K, V>();
      this.reverseMap = new Map<V, K>();
      return;
    }

    const [keys, vals] = _.zip(...source);
    if (
      _.uniq(keys).length !== keys.length ||
      _.uniq(vals).length !== vals.length
    ) {
      throw new Error('BiMap cannot have duplicate keys or values');
    }
    const reverseSource = _.zip(vals as V[], keys as K[]) as [V, K][];

    this.forwardMap = new Map<K, V>(source);
    this.reverseMap = new Map<V, K>(reverseSource);
  }

  public get size(): number {
    return this.forwardMap.size;
  }

  public set(key: K, value: V): void {
    if (this.forwardMap.has(key)) {
      const foundVal = this.forwardMap.get(key);
      if (foundVal !== undefined) this.reverseMap.delete(foundVal);
    }
    if (this.reverseMap.has(value)) {
      const foundKey = this.reverseMap.get(value);
      if (foundKey !== undefined) {
        this.forwardMap.delete(foundKey);
      }
    }
    this.forwardMap.set(key, value);
    this.reverseMap.set(value, key);
  }

  public get(key: K | unknown): Opt<V> {
    return this.forwardMap.get(key as K);
  }

  public getKey(value: V | unknown): Opt<K> {
    return this.reverseMap.get(value as V);
  }

  public clear(): void {
    this.forwardMap.clear();
    this.reverseMap.clear();
  }

  private deletePair(key: K, value: V): boolean {
    const resF = this.forwardMap.delete(key);
    const resR = this.reverseMap.delete(value);
    if (!(resF && resR)) {
      throw new Error('delete operation failed, BiMap is broken');
    }
    return true;
  }

  public delete(key: K): boolean {
    const value = this.forwardMap.get(key);
    if (value === undefined) {
      return false;
    }
    return this.deletePair(key, value);
  }

  public deleteValue(value: V): boolean {
    const key = this.reverseMap.get(value);
    if (key === undefined) {
      return false;
    }
    return this.deletePair(key, value);
  }

  public entries(): [K, V][] {
    return Array.from(this.forwardMap.entries());
  }

  public has(key: K): boolean {
    return this.forwardMap.has(key);
  }

  public hasValue(value: V): boolean {
    return this.reverseMap.has(value);
  }

  public keys(): K[] {
    return Array.from(this.forwardMap.keys());
  }

  public values(): V[] {
    return Array.from(this.forwardMap.values());
  }
}
